import * as React from "react";
import classnames from "classnames";
import { Label } from "../Components/Label";
import { IAdditionalFieldProps, IValidation } from "@cak-legacy/generation-core";
import { HintPopup } from "../Components/HintPopup";
import { useFormPropsContext } from "../Context/FormPropsContextProvider";
import { useFlexDirectionFormGroupContext } from "../Context/FlexDirectionFormGroupContextProvider";
import { convertCallbackToValidationArray } from "../Utils/validate";
import { HasMaxLengthValidation } from "../Validations/HasMaxlengthValidation";
import { DefaultFieldRenderer } from "../Interfaces/defaultFieldRenderer";
import { ErrorView } from "../Components/ErrorView";

function findMaxLengthValidation(value: IValidation | null | undefined): value is HasMaxLengthValidation {
  if (value instanceof HasMaxLengthValidation) {
    return value.limitMaxLengthOnInputs || false;
  }
  return false;
}

export const defaultFieldRenderer: DefaultFieldRenderer = (props) => {
  const validations = convertCallbackToValidationArray(props.field.validate);

  const flexDirectionFormGroupControlContext = useFlexDirectionFormGroupContext();
  const flexDirection =
    props.field.ui?.formOverview?.flexDirection ??
    props.field.ui?.flexDirection ??
    flexDirectionFormGroupControlContext;
  const formProps = useFormPropsContext();

  const fieldType = props.field.type;
  const formgroupClasses = classnames(
    "form-group",
    props.field.ui?.formGroupfullWidth ? "form-group-full-width" : "",
    props.field.ui?.formOverview?.formGroupCustomCssClass ?? "",
    props.field.ui?.className ?? ""
  );

  const labelClassName = props.field.popupHint ? " input__label-with-hint" : "";
  const errorClassName = props.error ? " input__has-error" : "";

  const popupHint = props.field.popupHint ? (
    <HintPopup fieldId={props.name} popupHint={props.field.popupHint} />
  ) : null;

  const additionalProps: IAdditionalFieldProps = {
    ariaDescribedBy: props.field.popupHint ? `${props.name}ToolTip` : undefined,
    maxLength: validations?.find(findMaxLengthValidation)?.maxLength ?? undefined
  };

  let beforeFieldContent: JSX.Element | null = props.field.beforeFieldContent ? (
    <>{props.field.beforeFieldContent}</>
  ) : null;

  let afterFieldContent: JSX.Element | null = props.field.afterFieldContent ? (
    <>{props.field.afterFieldContent}</>
  ) : null;

  const errorField = props.error ? <ErrorView error={props.error} /> : <></>;

  return fieldType !== "radio" ? (
    <div className={formgroupClasses}>
      {beforeFieldContent}
      <div
        id={`field-${props.name}`}
        className={`input__${props.field.type} flexdirection-${flexDirection}${errorClassName}`}
      >
        <Label
          type={fieldType}
          name={props.name}
          text={props.label}
          popupHint={popupHint}
          className={labelClassName}
          maxLength={additionalProps.maxLength}
          counterValue={props.value?.length}
          counterUnit={formProps?.fieldDefaults?.textArea?.counterUnit}
          textAreaCounter={formProps?.fieldDefaults?.textArea?.counterAndMaxLengthMessage}
        />
        <props.Component {...props.componentProps} {...additionalProps} />
        {props.field.infoText && (
          <span className="alert info-text info-icon-outline">{props.field.infoText}</span>
        )}
        {errorField}
      </div>
      {afterFieldContent}
    </div>
  ) : (
    <div className={formgroupClasses}>
      {beforeFieldContent}
      <div
        id={`field-${props.name}`}
        className={`input__${props.field.type} flexdirection-${flexDirection}${errorClassName}`}
        aria-labelledby={`radiogroup-label-${props.name}`}
      >
        <Label
          type={fieldType}
          name={props.name}
          text={props.label}
          popupHint={popupHint}
          className={labelClassName}
          maxLength={additionalProps.maxLength}
          counterValue={props.value?.length}
          counterUnit={formProps?.fieldDefaults?.textArea?.counterUnit}
          textAreaCounter={formProps?.fieldDefaults?.textArea?.counterAndMaxLengthMessage}
        />

        <props.Component {...props.componentProps} />
        {errorField}
      </div>
      {afterFieldContent}
    </div>
  );
};
